/**
 * Web-font harvesting + inlining for screenshots.
 *
 * WHY THIS EXISTS
 * ---------------
 * `modern-screenshot` renders the page into an SVG <foreignObject> which is then
 * drawn as an <img>. An SVG loaded as an image is a sandbox: it CANNOT fetch any
 * external resource. So every web font has to be baked into the SVG as a
 * `data:` URI, or the browser silently falls back to a system font — the
 * screenshot then has different glyph widths and line heights than the real
 * page, and any layout bug the reporter is trying to show (text too close to an
 * image, an overflowing button, a broken column) no longer reproduces in the
 * shot. That is exactly the "fonts are not the same" complaint.
 *
 * The library's own font embedding only sees stylesheets whose `cssRules` are
 * script-readable. A cross-origin <link> — Google Fonts, Adobe Fonts, a CDN,
 * any font CSS served from another domain — throws SecurityError on `cssRules`,
 * so the library skips it entirely and the font never gets embedded. That is the
 * single most common case on real customer sites.
 *
 * WHAT WE DO
 * ----------
 *   1. Read @font-face rules from every same-origin stylesheet (as the library
 *      does), AND re-fetch cross-origin stylesheets over the network so their
 *      rules become readable (the CSS file itself is nearly always CORS-open,
 *      it's only the CSSOM that's restricted).
 *   2. Keep only the families the page actually uses, narrowed to the exact
 *      faces the browser reports as loaded (`document.fonts`) — this drops the
 *      dozens of unused unicode-range subsets Google Fonts ships.
 *   3. Download each remaining font file and rebuild the @font-face rule with a
 *      `data:` URI.
 *
 * The result is handed to `modern-screenshot` as `font.cssText`. Note that with
 * `cssText` the library inlines the string VERBATIM (it does not resolve URLs
 * itself), which is precisely why we must produce data URIs here.
 *
 * FAILURE POSTURE: any font we cannot fetch (CDN without CORS headers) is
 * dropped and we fall back to the library's default behaviour rather than
 * shipping a half-built font sheet.
 */

/** Total base64-inlined font budget. Enough for ~15 woff2 subsets. */
const MAX_TOTAL_FONT_BYTES = 3_000_000;
/** Per-request ceiling, so one hung CDN can't stall the whole capture. */
const FETCH_TIMEOUT_MS = 4_000;
/** Cap the DOM walk that determines which families are in use. */
const MAX_ELEMENTS_SCANNED = 4_000;

type FontFaceInfo = {
  /** Unquoted, lowercased family name — the grouping/matching key. */
  family: string;
  /** Everything except `src`, ready to be re-emitted. */
  descriptors: string[];
  /** Raw `src` value with all URLs already absolutized. */
  src: string;
  weight: string;
  style: string;
  unicodeRange: string;
};

/** url() → data: URI, deduped across captures (fonts don't change per shot). */
const fontDataUrlCache = new Map<string, string | null>();
/** The finished cssText, invalidated when the page's font/stylesheet set changes. */
let cssTextCache: { signature: string; cssText: string } | null = null;

const URL_ENTRY_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)(?:\s*format\(\s*(['"]?)([^'")]+)\3\s*\))?/gi;
const ANY_URL_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

/** Descriptors worth preserving; anything else is noise for a screenshot. */
const KEPT_DESCRIPTORS = [
  "font-family",
  "font-style",
  "font-weight",
  "font-stretch",
  "font-display",
  "unicode-range",
  "font-variant",
  "font-feature-settings",
  "font-variation-settings",
  "ascent-override",
  "descent-override",
  "line-gap-override",
  "size-adjust",
];

function normalizeFamily(value: string): string {
  return value
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase();
}

function normalizeDescriptor(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}

function faceKey(family: string, weight: string, style: string, unicodeRange: string): string {
  return [
    normalizeFamily(family),
    normalizeDescriptor(weight) || "400",
    normalizeDescriptor(style) || "normal",
    normalizeDescriptor(unicodeRange),
  ].join("|");
}

/** Rewrite relative url()s against the stylesheet they came from. */
function absolutizeUrls(cssText: string, base: string | null): string {
  if (!base) return cssText;
  return cssText.replace(ANY_URL_RE, (raw, _quote: string, url: string) => {
    if (/^(data:|blob:|https?:|about:|#)/i.test(url)) return raw;
    try {
      return `url("${new URL(url, base).href}")`;
    } catch {
      return raw;
    }
  });
}

function isFontFaceRule(rule: CSSRule): rule is CSSFontFaceRule {
  return typeof CSSFontFaceRule !== "undefined" && rule instanceof CSSFontFaceRule;
}

function isImportRule(rule: CSSRule): rule is CSSImportRule {
  return typeof CSSImportRule !== "undefined" && rule instanceof CSSImportRule;
}

/**
 * Walk a rule list, descending through @media / @supports / @layer wrappers,
 * collecting every @font-face that actually references a file.
 */
function collectFontFaceRules(rules: CSSRuleList, base: string | null, out: FontFaceInfo[]): void {
  for (const rule of Array.from(rules)) {
    if (isFontFaceRule(rule)) {
      const src = rule.style.getPropertyValue("src");
      if (!src || !/url\(/i.test(src)) continue; // local()-only face — nothing to embed
      const family = rule.style.getPropertyValue("font-family");
      if (!family) continue;

      const descriptors: string[] = [];
      for (const name of KEPT_DESCRIPTORS) {
        const value = rule.style.getPropertyValue(name);
        if (value) descriptors.push(`${name}:${value}`);
      }

      out.push({
        family: normalizeFamily(family),
        descriptors,
        src: absolutizeUrls(src, base),
        weight: rule.style.getPropertyValue("font-weight"),
        style: rule.style.getPropertyValue("font-style"),
        unicodeRange: rule.style.getPropertyValue("unicode-range"),
      });
      continue;
    }

    const nested = (rule as CSSGroupingRule).cssRules;
    if (nested) collectFontFaceRules(nested, base, out);
  }
}

/** Parse raw CSS text into a real (detached) stylesheet so we get CSSOM objects. */
function parseCssText(cssText: string): CSSStyleSheet | null {
  try {
    const doc = document.implementation.createHTMLDocument("");
    const style = doc.createElement("style");
    style.textContent = cssText;
    doc.head.appendChild(style);
    return style.sheet;
  } catch {
    return null;
  }
}

async function fetchWithTimeout(url: string, as: "text"): Promise<string | null>;
async function fetchWithTimeout(url: string, as: "blob"): Promise<Blob | null>;
async function fetchWithTimeout(url: string, as: "text" | "blob"): Promise<string | Blob | null> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetch(url, {
      mode: "cors",
      credentials: "omit",
      cache: "force-cache",
      signal: controller?.signal,
    });
    if (!res.ok) return null;
    return as === "text" ? await res.text() : await res.blob();
  } catch {
    return null; // no CORS headers, offline, aborted — caller falls back
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Re-fetch a stylesheet we couldn't read through the CSSOM, following one level
 * of @import (deep import chains are vanishingly rare for font CSS).
 */
async function harvestRemoteSheet(href: string, out: FontFaceInfo[], depth = 0): Promise<void> {
  const cssText = await fetchWithTimeout(href, "text");
  if (!cssText) return;
  const sheet = parseCssText(absolutizeUrls(cssText, href));
  if (!sheet) return;

  collectFontFaceRules(sheet.cssRules, href, out);

  if (depth >= 1) return;
  const imports: string[] = [];
  for (const rule of Array.from(sheet.cssRules)) {
    if (!isImportRule(rule) || !rule.href) continue;
    try {
      imports.push(new URL(rule.href, href).href);
    } catch {
      /* malformed @import — ignore */
    }
  }
  await Promise.all(imports.map((url) => harvestRemoteSheet(url, out, depth + 1)));
}

/** Every @font-face on the page, from readable and cross-origin stylesheets alike. */
async function harvestAllFontFaces(): Promise<FontFaceInfo[]> {
  const out: FontFaceInfo[] = [];
  const remote: string[] = [];

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      // Throws SecurityError for cross-origin sheets — that's the signal to refetch.
      const rules = sheet.cssRules;
      collectFontFaceRules(rules, sheet.href ?? document.baseURI, out);
    } catch {
      if (sheet.href) remote.push(sheet.href);
    }
  }

  await Promise.all(remote.map((href) => harvestRemoteSheet(href, out)));
  return out;
}

/** Families referenced by the live DOM, so unused @font-face blocks are skipped. */
function usedFontFamilies(): Set<string> {
  const used = new Set<string>();
  let scanned = 0;
  const walk = (root: ParentNode) => {
    for (const el of Array.from(root.querySelectorAll("*"))) {
      if (scanned++ > MAX_ELEMENTS_SCANNED) return;
      const family = getComputedStyle(el).fontFamily;
      if (!family) continue;
      for (const name of family.split(",")) used.add(normalizeFamily(name));
      const shadow = (el as HTMLElement).shadowRoot;
      if (shadow) walk(shadow);
    }
  };
  try {
    used.add(normalizeFamily(getComputedStyle(document.body).fontFamily));
    walk(document.body);
  } catch {
    /* fall through with whatever we collected */
  }
  return used;
}

/** Keys of the faces the browser has actually loaded (i.e. the used subsets). */
function loadedFaceKeys(): Set<string> {
  const keys = new Set<string>();
  const fonts = document.fonts;
  if (!fonts || typeof fonts.forEach !== "function") return keys;
  try {
    fonts.forEach((face) => {
      if (face.status !== "loaded") return;
      keys.add(faceKey(face.family, face.weight, face.style, face.unicodeRange));
    });
  } catch {
    /* older browsers — no narrowing, we just embed the whole family */
  }
  return keys;
}

const FORMAT_PREFERENCE = ["woff2", "woff", "opentype", "truetype", "embedded-opentype", "svg"];

const FORMAT_MIME: Record<string, string> = {
  woff2: "font/woff2",
  woff: "font/woff",
  truetype: "font/ttf",
  ttf: "font/ttf",
  opentype: "font/otf",
  otf: "font/otf",
  "embedded-opentype": "application/vnd.ms-fontobject",
  svg: "image/svg+xml",
};

function inferFormat(url: string): string {
  const ext = (url.split(/[?#]/)[0] ?? "").split(".").pop()?.toLowerCase() ?? "";
  if (ext === "ttf") return "truetype";
  if (ext === "otf") return "opentype";
  if (ext === "eot") return "embedded-opentype";
  return ext;
}

/**
 * Pick ONE source per face — the smallest modern format the browser would have
 * used anyway. Embedding every fallback format would multiply the payload for
 * no visual gain.
 */
function pickSource(src: string): { url: string; format: string } | null {
  const entries: { url: string; format: string }[] = [];
  URL_ENTRY_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_ENTRY_RE.exec(src))) {
    const url = match[2];
    if (!url) continue;
    entries.push({ url, format: (match[4] || inferFormat(url)).toLowerCase() });
  }
  for (const preferred of FORMAT_PREFERENCE) {
    const hit = entries.find((e) => e.format === preferred);
    if (hit) return hit;
  }
  return entries[0] ?? null;
}

async function toDataUrl(url: string, format: string): Promise<string | null> {
  const cached = fontDataUrlCache.get(url);
  if (cached !== undefined) return cached;

  const blob = await fetchWithTimeout(url, "blob");
  if (!blob || blob.size > MAX_TOTAL_FONT_BYTES) {
    fontDataUrlCache.set(url, null);
    return null;
  }

  const dataUrl = await new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

  // Servers frequently return application/octet-stream for fonts; the SVG
  // renderer is stricter than the CSS engine, so restate the real font MIME.
  const mime = FORMAT_MIME[format];
  const normalized =
    dataUrl && mime ? dataUrl.replace(/^data:[^;]*;/, `data:${mime};`) : dataUrl;

  fontDataUrlCache.set(url, normalized);
  return normalized;
}

/**
 * Build a self-contained @font-face stylesheet (all sources as data URIs) for
 * the fonts the page is currently rendering with.
 *
 * Returns "" when nothing could be embedded — the caller should then let
 * `modern-screenshot` fall back to its own (same-origin-only) handling.
 */
export async function collectEmbeddedFontCss(): Promise<string> {
  const signature = `${document.styleSheets.length}:${document.fonts?.size ?? 0}`;
  if (cssTextCache && cssTextCache.signature === signature) return cssTextCache.cssText;

  let cssText = "";
  try {
    const faces = await harvestAllFontFaces();
    if (faces.length) {
      const used = usedFontFamilies();
      const loaded = loadedFaceKeys();

      // Group by family so the "loaded subsets" narrowing can fall back to the
      // whole family whenever the browser gives us nothing to match against.
      const byFamily = new Map<string, FontFaceInfo[]>();
      for (const face of faces) {
        if (used.size && !used.has(face.family)) continue;
        const list = byFamily.get(face.family);
        if (list) list.push(face);
        else byFamily.set(face.family, [face]);
      }

      const chosen: FontFaceInfo[] = [];
      for (const list of byFamily.values()) {
        const exact = list.filter((f) =>
          loaded.has(faceKey(f.family, f.weight, f.style, f.unicodeRange)),
        );
        chosen.push(...(exact.length ? exact : list));
      }

      let budget = MAX_TOTAL_FONT_BYTES;
      const blocks = await Promise.all(
        chosen.map(async (face) => {
          const source = pickSource(face.src);
          if (!source) return null;
          const dataUrl = await toDataUrl(source.url, source.format);
          if (!dataUrl) return null;
          // Rough decoded size; base64 is ~4/3 of the bytes it encodes.
          budget -= dataUrl.length * 0.75;
          if (budget < 0) return null;
          const format = FORMAT_MIME[source.format] ? `${source.format}` : "";
          const src = format
            ? `url(${dataUrl}) format("${format}")`
            : `url(${dataUrl})`;
          return `@font-face{${face.descriptors.join(";")};src:${src};}`;
        }),
      );

      cssText = blocks.filter(Boolean).join("\n");
    }
  } catch {
    cssText = ""; // never let font work break the screenshot itself
  }

  cssTextCache = { signature, cssText };
  return cssText;
}

/**
 * Give in-flight web fonts a chance to finish before we snapshot; capturing
 * mid-swap would bake the fallback font into the image.
 */
export async function waitForFontsReady(timeoutMs = 1_500): Promise<void> {
  if (!document.fonts?.ready) return;
  await Promise.race([
    document.fonts.ready.catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}
